import React, { useEffect, useState } from 'react'

interface DebouncedSearchBarProps {
	value?: string
	onChange: (value: string) => void
	placeholder?: string
	debounceMs?: number
	minSearchLength?: number
	name?: string
	id?: string
	className?: string
	disabled?: boolean
}

const DebouncedSearchBar: React.FC<DebouncedSearchBarProps> = ({
	value = '',
	onChange,
	placeholder = 'Search...',
	debounceMs = 400,
	minSearchLength = 3,
	name = 'search',
	id = 'search',
	className = '',
	disabled = false,
}) => {
	const [inputValue, setInputValue] = useState(value)

	useEffect(() => {
		setInputValue(value)
	}, [value])

	useEffect(() => {
		const timeout = window.setTimeout(() => {
			const trimmedValue = inputValue.trim()
			if (trimmedValue.length > 0 && trimmedValue.length < minSearchLength) return
			onChange(inputValue)
		}, debounceMs)

		return () => window.clearTimeout(timeout)
	}, [debounceMs, inputValue, minSearchLength, onChange])

	const showClear = inputValue.length > 0 && !disabled

	return (
		<div className={`relative w-full max-w-md ${className}`.trim()}>
			<i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-[hsl(var(--muted-foreground))]" aria-hidden />
			<input
				type="text"
				name={name}
				id={id}
				value={inputValue}
				disabled={disabled}
				placeholder={placeholder}
				onChange={(e) => setInputValue(e.target.value)}
				className="h-11 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-10 text-sm text-[hsl(var(--foreground))] shadow-sm outline-none transition placeholder:text-[hsl(var(--muted-foreground))] focus:border-[hsl(var(--primary))] focus:ring-4 focus:ring-[hsl(var(--primary))]/10 disabled:cursor-not-allowed disabled:opacity-60"
			/>
			{showClear && (
				<button
					type="button"
					className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] transition hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]"
					onClick={() => setInputValue('')}
					aria-label="Clear search"
				>
					<i className="ri-close-line text-base" aria-hidden />
				</button>
			)}
		</div>
	)
}

export default DebouncedSearchBar
